"use client"

import type React from "react"
import { useState } from "react"
import { X, Check, Sparkles, MessageCircle, Users, BarChart3 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useUserStore } from "@/lib/stores/user-store"

interface SignupModalProps {
  isOpen: boolean
  onClose: () => void
  trigger?: "chat_limit" | "save_progress" | "section_complete" | "owner_profile_complete"
}

export function SignupModal({ isOpen, onClose, trigger = "chat_limit" }: SignupModalProps) {
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const { signUp, ownerProfileData } = useUserStore()

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !name) return

    setIsLoading(true)
    await new Promise((resolve) => setTimeout(resolve, 800))
    signUp(email, name)
    setIsLoading(false)
    onClose()
  }

  const handleGoogleSignIn = async () => {
    setIsLoading(true)
    await new Promise((resolve) => setTimeout(resolve, 800))
    signUp("user@gmail.com", ownerProfileData?.name || "Google User")
    setIsLoading(false)
    onClose()
  }

  const getTriggerContent = () => {
    switch (trigger) {
      case "owner_profile_complete":
        return {
          title: "Great progress!",
          subtitle: "You've completed your Identity & Owner Profile.",
          benefits: [
            { icon: MessageCircle, text: "Unlock AI chat (25 messages/day)" },
            { icon: Users, text: "Add your team members" },
            { icon: BarChart3, text: "Access basic insights dashboard" },
            { icon: Check, text: "Save progress permanently" },
          ],
        }
      case "save_progress":
        return {
          title: "Save your work",
          subtitle: "Your session expires in 12 hours.",
          benefits: [
            { icon: Check, text: "Save your progress permanently" },
            { icon: MessageCircle, text: "Get 25 chat messages per day" },
            { icon: Users, text: "Complete your full business profile" },
            { icon: BarChart3, text: "Access basic insights dashboard" },
          ],
        }
      default:
        return {
          title: "Create your account",
          subtitle: "Continue building your business profile.",
          benefits: [
            { icon: Check, text: "Save your progress permanently" },
            { icon: MessageCircle, text: "Get 25 chat messages per day" },
            { icon: Users, text: "Complete your full business profile" },
            { icon: BarChart3, text: "Access basic insights dashboard" },
          ],
        }
    }
  }

  const content = getTriggerContent()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-md rounded-2xl bg-background border border-[var(--border-light)] p-5 sm:p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 rounded-lg text-[var(--text-secondary)] hover:bg-[var(--surface)] motion-fast"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="text-center mb-5 sm:mb-6">
          <div className="flex justify-center mb-4">
            <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-2xl bg-[var(--cta-light)] flex items-center justify-center">
              <Sparkles className="h-6 w-6 sm:h-7 sm:w-7 text-[var(--cta)]" strokeWidth={1.5} />
            </div>
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-2">{content.title}</h2>
          <p className="text-sm sm:text-body text-[var(--text-secondary)]">{content.subtitle}</p>
        </div>

        {/* Benefits - smaller on mobile */}
        <div className="space-y-2.5 sm:space-y-3 mb-5 sm:mb-6">
          <p className="text-caption text-[var(--text-secondary)] mb-2">Create a free account to:</p>
          {content.benefits.map((benefit, i) => (
            <div key={i} className="flex items-center gap-2.5 sm:gap-3">
              <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg bg-[var(--surface)] flex items-center justify-center shrink-0">
                <benefit.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[var(--cta)]" strokeWidth={1.5} />
              </div>
              <span className="text-sm sm:text-body text-foreground">{benefit.text}</span>
            </div>
          ))}
        </div>

        <Button
          onClick={handleGoogleSignIn}
          disabled={isLoading}
          variant="outline"
          className="w-full h-12 sm:h-12 mb-4 border-[var(--border-medium)] hover:bg-[var(--surface)] bg-transparent"
        >
          <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continue with Google
        </Button>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 h-px bg-[var(--border-light)]" />
          <span className="text-caption text-[var(--text-tertiary)]">or</span>
          <div className="flex-1 h-px bg-[var(--border-light)]" />
        </div>

        {/* Email Form */}
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          <Input
            type="text"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-12"
            required
          />
          <Input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-12"
            required
          />
          <Button
            type="submit"
            disabled={isLoading || !email || !name}
            className="w-full h-12 bg-[var(--cta)] hover:bg-[var(--cta-hover)]"
          >
            {isLoading ? "Creating account..." : "Create Free Account"}
          </Button>
        </form>

        <p className="text-center text-caption text-[var(--text-tertiary)] mt-4">No credit card required</p>

        <button
          onClick={onClose}
          className="w-full text-center text-caption text-[var(--text-secondary)] hover:text-foreground mt-4 py-2 motion-fast"
        >
          Maybe later
        </button>
      </div>
    </div>
  )
}
